import { useEffect, useRef } from 'react';

// Horizontal, auto-scrolling, infinite-loop carousel for the protocol principles.
// Shows ~3-4 cards at once, keeps moving on its own, pauses on hover, and can be dragged.
export default function PrincipiosCarousel({ items }) {
  const scrollRef = useRef(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const paused = useRef(false);

  // Triplicate so the middle copy can loop seamlessly in both directions.
  const cards = [...items, ...items, ...items];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const init = () => {
      const oneSet = el.scrollWidth / 3;
      if (oneSet > 0) el.scrollLeft = oneSet;
    };
    const rafInit = requestAnimationFrame(init);

    let rafId;
    const step = () => {
      if (el && !paused.current && !drag.current.down) {
        el.scrollLeft += 0.5; // gentle continuous drift
      }
      if (el) {
        const oneSet = el.scrollWidth / 3;
        if (oneSet > 0) {
          if (el.scrollLeft >= oneSet * 2) el.scrollLeft -= oneSet;
          else if (el.scrollLeft <= 0) el.scrollLeft += oneSet;
        }
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);

    window.addEventListener('resize', init);
    return () => {
      cancelAnimationFrame(rafInit);
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', init);
    };
  }, []);

  const onMouseDown = (e) => {
    const el = scrollRef.current;
    drag.current = { down: true, startX: e.pageX, startScroll: el.scrollLeft, moved: false };
    el.style.cursor = 'grabbing';
  };
  const onMouseMove = (e) => {
    if (!drag.current.down) return;
    e.preventDefault();
    const el = scrollRef.current;
    el.scrollLeft = drag.current.startScroll - (e.pageX - drag.current.startX);
  };
  const endDrag = () => {
    drag.current.down = false;
    const el = scrollRef.current;
    if (el) el.style.cursor = 'grab';
  };

  return (
    <div
      className="relative -mx-6 md:-mx-12"
      onMouseEnter={() => { paused.current = true; }}
      onMouseLeave={() => { paused.current = false; endDrag(); }}
    >
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-20 z-10 bg-gradient-to-r from-[#F5F5F3] to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-20 z-10 bg-gradient-to-l from-[#F5F5F3] to-transparent" />

      <div
        ref={scrollRef}
        data-testid="principios-carousel"
        className="flex items-stretch gap-5 overflow-x-auto no-scrollbar px-6 md:px-12 cursor-grab"
        style={{ scrollBehavior: 'auto' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
      >
        {cards.map((p, i) => (
          <div
            key={`${p.title}-${i}`}
            data-testid={`principio-card-${i}`}
            className="flex-shrink-0 w-[78vw] sm:w-[320px] bg-white border border-[#00163E]/10 rounded-sm p-6"
          >
            <p.icon size={22} className="text-[#C58C40] mb-4" strokeWidth={1.5} />
            <h3 className="text-[#00163E] text-base font-medium mb-2">{p.title}</h3>
            <p className="text-slate-500 text-sm leading-relaxed">{p.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
